import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { providersApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Search,
  Star,
  Phone,
  Mail,
  MapPin,
  Wrench,
  Zap,
  Leaf,
  Sparkles,
  Hammer,
  Bug,
  Shield,
  Paintbrush,
  Key,
  Thermometer,
  ChevronRight,
  CheckCircle2,
} from 'lucide-react';

const serviceTypes = [
  { id: 'all', label: 'All', icon: Wrench },
  { id: 'plumber', label: 'Plumber', icon: Wrench },
  { id: 'electrician', label: 'Electrician', icon: Zap },
  { id: 'gardener', label: 'Gardener', icon: Leaf },
  { id: 'cleaner', label: 'Cleaner', icon: Sparkles },
  { id: 'handyman', label: 'Handyman', icon: Hammer },
  { id: 'pest_control', label: 'Pest Control', icon: Bug },
];

const getServiceIcon = (type) => {
  const icons = {
    plumber: Wrench,
    electrician: Zap,
    gardener: Leaf,
    cleaner: Sparkles,
    handyman: Hammer,
    pest_control: Bug,
    security: Shield,
    painter: Paintbrush,
    locksmith: Key,
    hvac: Thermometer,
  };
  return icons[type] || Wrench;
};

export default function ServiceProviders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [activeType, setActiveType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);

  const hoaId = user?.hoa_id || 'demo_hoa';

  useEffect(() => {
    fetchProviders();
  }, [activeType]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const serviceType = activeType === 'all' ? null : activeType;
      const data = await providersApi.list(hoaId, serviceType).catch(() => getDemoProviders());
      setProviders(data);
    } catch (error) {
      setProviders(getDemoProviders());
    } finally {
      setLoading(false);
    }
  };

  const getDemoProviders = () => [
    { provider_id: 'prov_001', name: 'QuickFix Plumbing', service_type: 'plumber', phone: '+1 555-0101', email: 'contact@quickfixplumbing.com', description: '24/7 emergency plumbing services. Pipe repairs, leak detection, water heater installation.', rating: 4.8, reviews_count: 24, is_verified: true },
    { provider_id: 'prov_002', name: 'Bright Spark Electric', service_type: 'electrician', phone: '+1 555-0102', email: 'info@brightsparkelectric.com', description: 'Licensed electrician for residential and commercial. Panel upgrades, rewiring, smart home installation.', rating: 4.6, reviews_count: 18, is_verified: true },
    { provider_id: 'prov_003', name: 'Green Thumb Gardens', service_type: 'gardener', phone: '+1 555-0103', email: 'hello@greenthumb.com', description: 'Professional landscaping and garden maintenance. Weekly lawn care, tree trimming, irrigation.', rating: 4.9, reviews_count: 32, is_verified: true },
    { provider_id: 'prov_004', name: 'SpotlessClean Pro', service_type: 'cleaner', phone: '+1 555-0104', email: 'book@spotlessclean.com', description: 'Deep cleaning, move-in/move-out cleaning, regular housekeeping services.', rating: 4.7, reviews_count: 45, is_verified: true },
    { provider_id: 'prov_005', name: 'HandyDan Services', service_type: 'handyman', phone: '+1 555-0105', description: 'General repairs, furniture assembly, mounting, small projects. No job too small!', rating: 4.5, reviews_count: 28, is_verified: false },
    { provider_id: 'prov_006', name: 'BugBusters Pest Control', service_type: 'pest_control', phone: '+1 555-0106', email: 'help@bugbusters.com', description: 'Rodent control, termite inspection, ant and roach treatment. Safe for pets and children.', rating: 4.4, reviews_count: 15, is_verified: true },
  ];

  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
    toast.success('Opening phone dialer...');
  };

  const handleEmail = (email) => {
    window.location.href = `mailto:${email}`;
    toast.success('Opening email client...');
  };

  const filteredProviders = searchQuery 
    ? providers.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.service_type.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : providers;

  if (selectedProvider) {
    const ServiceIcon = getServiceIcon(selectedProvider.service_type);
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setSelectedProvider(null)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Provider Details</h1>
          </div>
        </header>

        <main className="max-w-md mx-auto px-4 py-6 space-y-6">
          <Card data-testid="provider-detail-card">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-xl bg-sky-100 flex items-center justify-center">
                  <ServiceIcon className="w-8 h-8 text-sky-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold font-manrope text-slate-900">{selectedProvider.name}</h2>
                    {selectedProvider.is_verified && (
                      <CheckCircle2 className="w-5 h-5 text-sky-500" />
                    )}
                  </div>
                  <Badge className="mt-1 capitalize">{selectedProvider.service_type.replace('_', ' ')}</Badge>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span className="font-medium">{selectedProvider.rating}</span>
                    </div>
                    <span className="text-sm text-slate-500">({selectedProvider.reviews_count} reviews)</span>
                  </div>
                </div>
              </div>

              <p className="text-slate-600 mb-6">{selectedProvider.description}</p>

              <div className="space-y-3">
                <Button 
                  className="w-full h-12 bg-sky-500 hover:bg-sky-600 rounded-full"
                  onClick={() => handleCall(selectedProvider.phone)}
                  data-testid="call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call {selectedProvider.phone}
                </Button>
                {selectedProvider.email && (
                  <Button 
                    variant="outline"
                    className="w-full h-12 rounded-full"
                    onClick={() => handleEmail(selectedProvider.email)}
                    data-testid="email-btn"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Send Email
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Rating */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-medium text-slate-900 mb-3">Rating Breakdown</h3>
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((stars) => {
                  const percentage = stars === 5 ? 65 : stars === 4 ? 25 : stars === 3 ? 7 : stars === 2 ? 2 : 1;
                  return (
                    <div key={stars} className="flex items-center gap-2">
                      <span className="text-sm text-slate-500 w-3">{stars}</span>
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-amber-400 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400 w-8">{percentage}%</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const getBackPath = () => {
    if (user?.role === 'trustee' || user?.role === 'property_manager') return '/admin';
    if (user?.role === 'managing_agent' || user?.role === 'agent_admin') return '/agent';
    if (user?.role === 'platform_owner') return '/platform';
    return '/dashboard';
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <Link to={getBackPath()}>
            <Button variant="ghost" size="icon" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-lg font-semibold font-manrope text-slate-900">Service Providers</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search providers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 rounded-full"
            data-testid="search-input"
          />
        </div>

        {/* Service Types */}
        <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
          {serviceTypes.map((type) => (
            <Button
              key={type.id}
              variant={activeType === type.id ? 'default' : 'outline'}
              size="sm"
              className={`rounded-full whitespace-nowrap ${activeType === type.id ? 'bg-slate-900' : ''}`}
              onClick={() => setActiveType(type.id)}
              data-testid={`type-${type.id}`}
            >
              <type.icon className="w-4 h-4 mr-1" />
              {type.label}
            </Button>
          ))}
        </div>

        {/* Providers List */}
        <div className="space-y-3">
          {loading ? (
            <>
              <Skeleton className="h-28 rounded-xl" />
              <Skeleton className="h-28 rounded-xl" />
              <Skeleton className="h-28 rounded-xl" />
            </>
          ) : filteredProviders.length > 0 ? (
            filteredProviders.map((provider, index) => {
              const ServiceIcon = getServiceIcon(provider.service_type);
              return (
                <Card 
                  key={provider.provider_id}
                  className="overflow-hidden animate-slideUp cursor-pointer hover:shadow-md transition-shadow"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => setSelectedProvider(provider)}
                  data-testid={`provider-${provider.provider_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <ServiceIcon className="w-7 h-7 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900 truncate">{provider.name}</h3>
                          {provider.is_verified && (
                            <CheckCircle2 className="w-4 h-4 text-sky-500 flex-shrink-0" />
                          )}
                        </div>
                        <Badge variant="secondary" className="text-xs mt-1 capitalize">
                          {provider.service_type.replace('_', ' ')}
                        </Badge>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                            <span className="text-sm font-medium">{provider.rating}</span>
                          </div>
                          <span className="text-xs text-slate-400">({provider.reviews_count} reviews)</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1">No providers found</h3>
                <p className="text-slate-500 text-sm">Try a different search or category</p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
